import { produce } from 'immer'
import {
  GET_ORGANIZATION_MEMBERS_FAILURE,
  GET_ORGANIZATION_MEMBERS_INIT,
  GET_ORGANIZATION_MEMBERS_SUCCESS,
  GetPeopleMembersActions,
} from 'src/app/PeopleMembers/redux/peopleMembers.actions'
import {
  initialState as initialPeopleMembersState,
  PeopleMembersState,
} from 'src/app/PeopleMembers/redux/peopleMembers.reducer'
import { businessRulesRouteProps } from 'src/navigation/routeProps'
import {
  GET_ORGANIZATION_CURRENCIES_FAILURE,
  GET_ORGANIZATION_CURRENCIES_INIT,
  GET_ORGANIZATION_CURRENCIES_SUCCESS,
  GET_RECEIPT_RULES_FAILURE,
  GET_RECEIPT_RULES_INIT,
  GET_RECEIPT_RULES_SUCCESS,
  GetOrganizationCurrenciesActions,
  GetOrganizationsActions,
  GetReceiptRulesActions,
  PATCH_ORGANIZATIONS_FAILURE,
  PATCH_ORGANIZATIONS_INIT,
  PATCH_ORGANIZATIONS_SUCCESS,
  PatchOrganizationsActions,
  POST_RECEIPT_RULES_FAILURE,
  POST_RECEIPT_RULES_INIT,
  POST_RECEIPT_RULES_SUCCESS,
  PostReceiptRulesActions,
  PUT_OUT_OF_POCKET_EXPENSES_FAILURE,
  PUT_OUT_OF_POCKET_EXPENSES_INIT,
  PUT_OUT_OF_POCKET_EXPENSES_SUCCESS,
  PUT_RECEIPT_RULES_FAILURE,
  PUT_RECEIPT_RULES_INIT,
  PUT_RECEIPT_RULES_SUCCESS,
  PutOutOfPocketExpensesActions,
  PutReceiptRulesActions,
  REMOVE_RECEIPT_RULES_FORM_ERROR,
  RemoveReceiptRulesFormError,
} from 'src/redux/actions/data/organizations.actions'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface BusinessRulesState {
  isCardApprovalsBeingEdited: boolean
  isReceiptRulesBeingEdited: boolean
  fetchSuccess: boolean
  fetchFailure: boolean
  patchingOrganizations: boolean
  puttingOrganizations: boolean
  patchOrganizationsTopLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  orgMembers: PeopleMembersState
  gettingReceiptRules: boolean
  submittingReceiptRules: boolean
  gettingOrganizationCurrencies: boolean
  receiptRulesErrorDetailMap?: ErrorDetailMap<string>
  topLevelReceiptRulesError?: string
  topLevelOrganizationApproverError?: string
  patchingOrganizationApprover: boolean
  isOutOfPocketExpensesBeingEdited: boolean
}

export const initialState: BusinessRulesState = {
  isCardApprovalsBeingEdited: false,
  isReceiptRulesBeingEdited: false,
  fetchSuccess: false,
  fetchFailure: false,
  patchingOrganizations: false,
  puttingOrganizations: false,
  orgMembers: initialPeopleMembersState,
  gettingReceiptRules: false,
  submittingReceiptRules: false,
  gettingOrganizationCurrencies: false,
  patchingOrganizationApprover: false,
  isOutOfPocketExpensesBeingEdited: false,
}

export type Actions =
  | GetOrganizationsActions
  | PatchOrganizationsActions
  | GetPeopleMembersActions
  | GetReceiptRulesActions
  | GetOrganizationCurrenciesActions
  | PostReceiptRulesActions
  | PutReceiptRulesActions
  | RemoveReceiptRulesFormError
  | PutOutOfPocketExpensesActions

const CoreBusinessRulesReducer = (
  state = initialState,
  action: Actions,
): BusinessRulesState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case PATCH_ORGANIZATIONS_INIT: {
        draft.patchingOrganizations = true
        draft.patchOrganizationsTopLevelError = undefined
        return draft
      }

      case PATCH_ORGANIZATIONS_FAILURE: {
        const { topLevelError } = parseBackendErrors(action.payload)

        draft.patchingOrganizations = false
        draft.patchOrganizationsTopLevelError = topLevelError
        return draft
      }

      case PATCH_ORGANIZATIONS_SUCCESS: {
        draft.patchingOrganizations = false
        draft.patchOrganizationsTopLevelError = undefined
        return draft
      }

      case GET_ORGANIZATION_MEMBERS_INIT: {
        draft.orgMembers.loading = true
        return draft
      }

      case GET_ORGANIZATION_MEMBERS_SUCCESS: {
        draft.orgMembers.loading = false
        return draft
      }

      case GET_ORGANIZATION_MEMBERS_FAILURE: {
        draft.orgMembers.loading = false
        return draft
      }

      case GET_RECEIPT_RULES_INIT: {
        draft.gettingReceiptRules = true
        return draft
      }

      case GET_RECEIPT_RULES_FAILURE:
      case GET_RECEIPT_RULES_SUCCESS: {
        draft.gettingReceiptRules = false
        return draft
      }

      case GET_ORGANIZATION_CURRENCIES_INIT: {
        draft.gettingOrganizationCurrencies = true
        return draft
      }

      case GET_ORGANIZATION_CURRENCIES_FAILURE:
      case GET_ORGANIZATION_CURRENCIES_SUCCESS: {
        draft.gettingOrganizationCurrencies = false
        return draft
      }

      case REMOVE_RECEIPT_RULES_FORM_ERROR: {
        draft.receiptRulesErrorDetailMap = undefined
        draft.topLevelReceiptRulesError = undefined
        draft.isReceiptRulesBeingEdited = false
        return draft
      }

      case PUT_RECEIPT_RULES_INIT:
      case POST_RECEIPT_RULES_INIT: {
        draft.submittingReceiptRules = true
        draft.receiptRulesErrorDetailMap = undefined
        draft.topLevelReceiptRulesError = undefined
        return draft
      }

      case PUT_RECEIPT_RULES_SUCCESS:
      case POST_RECEIPT_RULES_SUCCESS: {
        draft.submittingReceiptRules = false
        draft.isReceiptRulesBeingEdited = false
        return draft
      }

      case PUT_RECEIPT_RULES_FAILURE:
      case POST_RECEIPT_RULES_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.submittingReceiptRules = false
        draft.receiptRulesErrorDetailMap = errors.errorDetailMap
        draft.topLevelReceiptRulesError = errors.topLevelError
        return draft
      }

      case PUT_OUT_OF_POCKET_EXPENSES_INIT: {
        draft.patchingOrganizationApprover = true
        draft.topLevelOrganizationApproverError = undefined

        return draft
      }

      case PUT_OUT_OF_POCKET_EXPENSES_SUCCESS: {
        draft.patchingOrganizationApprover = false

        return draft
      }

      case PUT_OUT_OF_POCKET_EXPENSES_FAILURE: {
        draft.patchingOrganizationApprover = false
        draft.topLevelOrganizationApproverError = action.payload.error

        return draft
      }
    }
  })
}

const BusinessRulesReducer = createBackgroundReducer(
  businessRulesRouteProps,
  initialState,
  CoreBusinessRulesReducer,
)

export default BusinessRulesReducer
